import { FluentProvider, mergeClasses, Subtitle1, makeStyles, tokens } from '@fluentui/react-components';
import * as React from 'react';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

import { UserSettingsMenu } from './components/header/UserSettingsMenu';
import { WelcomeDialog } from './components/shared/WelcomeDialog';
import {
    BackendProbe,
    ChatView,
    Error,
    Loading,
    Login,
    HistoryPage,
    SettingsView,
    MemoriesView,
    LicencesView,
} from './components/views';
import { AuthHelper, AuthType } from './libs/auth/AuthHelper';
import { useAppDispatch, useAppSelector } from './redux/app/hooks';
import { RootState } from './redux/app/store';
import { setShowCsat } from './redux/features/app/appSlice';
import { getParentTargetOrigin } from './libs/parentOrigin';
import '/node_modules/flag-icons/css/flag-icons.min.css';
import { AppState } from './AppState';
import './i18n';
import { HostContextSelector } from './components/header/HostContextSelector';
import { useMessageHandler } from './libs/hooks/useMessageHandler';
import { NewChatGuardProvider } from './libs/hooks/useRequestNewChat';
import SatisfactionRating from './components/shared/SatisfactionRating';
import { useEmbeddedMode } from './libs/hooks/useEmbeddedMode';
import { useAuthAndChatState } from './libs/hooks/useAuthAndChatState';
import { useInitialServiceInfo } from './libs/hooks/useInitialServiceInfo';
import { useInitialAnnouncements } from './libs/hooks/useInitialAnnouncements';
import { useAnnouncementsBadgeBridge } from './libs/hooks/useAnnouncementsBadgeBridge';
import { useInitialUserPreferences } from './libs/hooks/useInitialUserPreferences';
import { useInitialUserMemory } from './libs/hooks/useInitialUserMemory';
import { useAppTheme } from './libs/hooks/useAppTheme';
import { useSnowplowInit } from './libs/hooks/useSnowplowInit';
import { useSnowplowIdentity } from './libs/hooks/useSnowplowIdentity';
import { useSnowplowPageTracking } from './libs/hooks/useSnowplowPageTracking';

export const useClasses = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        overflow: 'hidden',
    },
    header: {
        alignItems: 'center',
        display: 'flex',
        '& h1': {
            paddingLeft: tokens.spacingHorizontalM,
            display: 'flex',
            alignItems: 'center',
            margin: 0,
            fontSize: tokens.fontSizeBase400,
            flex: '1 1 0',
        },
        '& img': {
            display: 'flex',
            width: '150px',
        },
        height: '48px',
        justifyContent: 'space-between',
        width: '100%',
    },
    headerTitle: {
        margin: '0',
    },
    hidden: {
        display: 'none',
    },
    persona: {
        marginRight: tokens.spacingHorizontalXXL,
    },
    cornerItems: {
        display: 'flex',
        gap: tokens.spacingHorizontalS,
    },
    headerItems: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
    },
    mainContent: {
        display: 'flex',
        flex: 1,
    },
    content: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    standaloneHeader: {
        background: 'var(--app-header-background)',
        color: tokens.colorNeutralForeground1,
    },
    loggedOutHeader: {
        background: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        boxShadow: tokens.shadow2,
    },
});

const OidcNavigationGuard: FC<{ children: React.ReactNode }> = ({ children }) => {
    const auth = useAuth();
    const { t } = useTranslation();

    // Handle OIDC navigation states
    switch (auth.activeNavigator) {
        case 'signinSilent':
            console.log('signinSilent mode on page');
            return <Loading text={t('app.signingIn')} />;
        case 'signoutRedirect':
            console.log('signoutRedirect mode on page');
            return <Loading text={t('app.signingOut')} />;
    }

    if (auth.isLoading) {
        return <Loading text={t('app.loading')} />;
    }

    if (auth.error) {
        return <Error text={auth.error.message} />;
    }

    return <>{children}</>;
};

const App: FC = () => {
    const classes = useClasses();
    const auth = useAuth();
    const { t } = useTranslation();
    const [appState, setAppState] = React.useState(AppState.SettingUserInfo);
    const { hostContext } = useAppSelector((state: RootState) => state.hostContext);
    const { embeddedMode } = useAppSelector((state: RootState) => state.app);
    const { className, theme, style } = useAppTheme();

    // Set the page title
    const title = t('app.title');
    useEffect(() => {
        document.title = title;
    }, [title]);

    useEmbeddedMode();
    useAuthAndChatState(appState, setAppState, hostContext);
    useInitialServiceInfo(hostContext);
    useInitialUserPreferences();
    useInitialUserMemory();
    useInitialAnnouncements();
    useAnnouncementsBadgeBridge();
    useSnowplowInit();

    const showHeader = !embeddedMode;
    const authType = AuthHelper.getAuthType();

    return (
        <FluentProvider className={className} theme={theme} style={style}>
            {authType == AuthType.OIDC && (
                <>
                    {!auth.isAuthenticated ? (
                        <div className={classes.container}>
                            <div className={mergeClasses(classes.header, classes.loggedOutHeader)}>
                                <Subtitle1 as="h1">{t('app.subTitle')}</Subtitle1>
                            </div>
                            {appState === AppState.SigningOut && <Loading text={t('app.signingOut')} />}
                            {appState !== AppState.SigningOut && <Login />}
                        </div>
                    ) : (
                        <Chat classes={classes} appState={appState} setAppState={setAppState} showHeader={showHeader} />
                    )}
                </>
            )}
            {authType == AuthType.None && (
                <Chat classes={classes} appState={appState} setAppState={setAppState} showHeader={showHeader} />
            )}
        </FluentProvider>
    );
};

const Chat = ({
    classes,
    appState,
    setAppState,
    showHeader,
}: {
    classes: ReturnType<typeof useClasses>;
    appState: AppState;
    setAppState: React.Dispatch<React.SetStateAction<AppState>>;
    showHeader: boolean;
}) => {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const showCsat = useAppSelector((state: RootState) => state.app.showCsat);
    const enableInChatCsat = useAppSelector((state: RootState) => state.app.serviceInfo.featureFlags.EnableInChatCsat);
    // The Minnen page (#2220) is gated on the same flag that gates its Settings section; when off the
    // route isn't mounted at all (a falsy child is ignored by <Routes>), so /memories simply 404s.
    const userMemoryEnabled = useAppSelector(
        (state: RootState) => state.app.serviceInfo.featureFlags.UserMemoryEnabled,
    );

    useSnowplowPageTracking();
    useSnowplowIdentity();

    useMessageHandler({
        setAppState,
        i18n,
        setShowCsat: (show: boolean) => dispatch(setShowCsat(show)),
    });

    // Stable callback so BackendProbe's effect deps don't change every render (would reset the probe interval).
    const handleBackendFound = React.useCallback(() => {
        if (AuthHelper.getAuthType() == AuthType.None) {
            setAppState(AppState.LoadingChats);
        } else {
            setAppState(AppState.SettingUserInfo);
        }
    }, [setAppState]);

    return (
        <NewChatGuardProvider>
            <div className={classes.container}>
                {showHeader && (
                    <div className={mergeClasses(classes.header, classes.standaloneHeader)}>
                        <Subtitle1 as="h1">{t('app.subTitle')}</Subtitle1>
                        <div className={classes.headerItems}>
                            <HostContextSelector />
                        </div>
                        <div className={classes.cornerItems}>
                            <UserSettingsMenu
                                setLoadingState={() => {
                                    setAppState(AppState.SigningOut);
                                }}
                                setAppState={setAppState}
                            />
                        </div>
                    </div>
                )}
                {appState === AppState.ProbeForBackend && (
                    <BackendProbe
                        uri={process.env.REACT_APP_BACKEND_URI as string}
                        onBackendFound={handleBackendFound}
                    />
                )}
                {appState === AppState.SettingUserInfo && <Loading text={t('app.loadingUserInfo')} />}
                {appState === AppState.ErrorLoadingUserInfo && <Error text={t('error.general')} />}
                {appState === AppState.LoadingChats && <Loading text={t('app.loadingChats')} />}
                {appState === AppState.Chat && (
                    <>
                        {showCsat && !enableInChatCsat ? (
                            <SatisfactionRating
                                isOpen={showCsat}
                                onClose={() => {
                                    dispatch(setShowCsat(false));
                                    // CWE-668 (#2113): pin the validated embedder origin instead of '*'.
                                    window.parent.postMessage({ event: 'hideCsat' }, getParentTargetOrigin());
                                }}
                            />
                        ) : (
                            <Routes>
                                <Route path="/" element={<ChatView setAppState={setAppState} />} />
                                <Route path="/history" element={<HistoryPage />} />
                                <Route path="/settings" element={<SettingsView />} />
                                {userMemoryEnabled && <Route path="/memories" element={<MemoriesView />} />}
                                <Route path="/licences" element={<LicencesView />} />
                            </Routes>
                        )}
                    </>
                )}
                <WelcomeDialog />
            </div>
        </NewChatGuardProvider>
    );
};

const AppWithAuth: FC = () => (
    <BrowserRouter>
        <OidcNavigationGuard>
            <App />
        </OidcNavigationGuard>
    </BrowserRouter>
);

export default AppWithAuth;
